import React, { useCallback, useEffect, useState } from "react";
import { Plus, PauseCircle, PlayCircle, RefreshCw, Trash2, X, Save, Globe, ExternalLink, Download, Mail, KeyRound, Zap, Loader2, Link2Off, Users, Merge, Eye, EyeOff, Copy, CheckCircle2, AlertTriangle, User as UserIcon, Lock, Calendar, RotateCw, BadgePercent } from "lucide-react";
import {
  adminListHosting, adminCreateHosting,
  adminSuspendHosting, adminUnsuspendHosting, adminRenewHosting, adminDeleteHosting,
  adminListCustomers, adminPlatformSync, adminHostingControlPanel, adminSendWelcome,
  adminHostingSso, adminUnlinkHosting, adminRelinkHosting, adminDeleteHostingCascade,
  adminUpdateHostingShare, adminConsolidateHosting,
  adminChangeStackPassword, adminRefreshStackUser, adminUpdateHosting,
  adminEmailCredentials, adminHostingDuplicates, adminMergeDuplicates,
  adminPlatformSyncWithMerge,
  adminSyncStackUser, adminBulkSyncStackUsers, adminBulkCreateSupportUsers,
  adminGetSupportCredentials, adminSetSupportCredentials,
  adminSetLockedRenewalPrice, adminBulkLockRenewalForCustomer,
  adminRetrySupportUser,
} from "../../lib/api";
import { useCMS } from "../../context/CMSContext";

export default function HostingPanel({ onToast }) {
  const { cms } = useCMS();
  const [list, setList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [renewing, setRenewing] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [bulkSyncing, setBulkSyncing] = useState(false);
  const [bulkCreatingSupport, setBulkCreatingSupport] = useState(false);
  const [perRowSyncing, setPerRowSyncing] = useState({}); // { hostingId: bool }
  const [supportCreds, setSupportCreds] = useState(null);   // { hosting, email, password, has_support_credentials }
  const [savingSupport, setSavingSupport] = useState(false);
  const [showSupportPw, setShowSupportPw] = useState(false);
  const [cpInfo, setCpInfo] = useState(null);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [pwChange, setPwChange] = useState({ newPassword: "", saving: false });
  const blank = { customer_id: "", plan_key: "", domain_name: "", billing_cycle: "annual", amount_inr: "", provision_now: true, notes: "" };
  const [form, setForm] = useState(blank);
  const [renewMonths, setRenewMonths] = useState(12);
  // Share modal
  const [sharing, setSharing] = useState(null); // hosting record being edited
  const [shareText, setShareText] = useState("");
  const [shareSaving, setShareSaving] = useState(false);
  // Consolidate modal
  const [consolidating, setConsolidating] = useState(false);
  const [consForm, setConsForm] = useState({ target_email: "", target_name: "", create_login_user: true, keep_previous_as_shared: true, cleanup_orphan_customers: true });
  const [consBusy, setConsBusy] = useState(false);
  // Credentials modal extras
  const [showPw, setShowPw] = useState(false);
  const [editPw, setEditPw] = useState(false);
  const [newPw, setNewPw] = useState("");
  const [savingPw, setSavingPw] = useState(false);
  const [pwErr, setPwErr] = useState("");
  const [copied, setCopied] = useState("");
  const [refreshingStack, setRefreshingStack] = useState(false);
  const [emailingCreds, setEmailingCreds] = useState(false);
  // Duplicates modal
  const [dupOpen, setDupOpen] = useState(false);
  const [dupLoading, setDupLoading] = useState(false);
  const [dupGroups, setDupGroups] = useState([]);
  const [dupKeeper, setDupKeeper] = useState({}); // { [domain]: hosting_id }
  // Expiry editor
  const [expiryEdit, setExpiryEdit] = useState(null); // { hosting, value }
  const [expirySaving, setExpirySaving] = useState(false);
  // Locked renewal price editor
  const [lockEdit, setLockEdit] = useState(null); // { hosting, value, applyAll }
  const [lockSaving, setLockSaving] = useState(false);

  const load = useCallback(async () => { setList(await adminListHosting()); }, []);
  const loadCustomers = useCallback(async () => { setCustomers(await adminListCustomers()); }, []);
  useEffect(() => { load(); loadCustomers(); }, [load, loadCustomers]);

  const plans = cms?.plans || [];

  const sync = async (autoMerge = false) => {
    setSyncing(true);
    try {
      const r = await adminPlatformSyncWithMerge(autoMerge);
      const parts = [
        `${r.imported_hosting} new`,
        `${r.relinked_hosting || 0} relinked`,
        `${r.imported_customers} customers`,
        `${r.packages_found} packages`,
      ];
      if (autoMerge) parts.push(`merged ${r.auto_merged_groups || 0} duplicate groups (${r.auto_dropped_rows || 0} dropped)`);
      onToast?.(`Sync done — ${parts.join(" · ")}`);
      await load();
      await loadCustomers();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Sync failed", "error");
    } finally {
      setSyncing(false);
    }
  };

  const syncWithPrompt = async () => {
    const merge = window.confirm(
      "Sync hosting packages from the platform.\n\n" +
      "Click OK to also auto-merge any same-domain duplicates after the sync " +
      "(the row already linked to 20i is kept; orphan emails become shared users).\n\n" +
      "Click Cancel to sync without merging."
    );
    await sync(merge);
  };

  const openCP = async (h) => {
    setShowPw(false); setEditPw(false); setNewPw(""); setPwErr(""); setCopied("");
    try {
      const info = await adminHostingControlPanel(h.id);
      setCpInfo({ ...info, hosting: h });
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Could not fetch control panel info", "error");
    }
  };

  const refreshCP = async () => {
    if (!cpInfo?.hosting?.id) return;
    try {
      const info = await adminHostingControlPanel(cpInfo.hosting.id);
      setCpInfo((prev) => ({ ...prev, ...info }));
    } catch (e) { /* keep modal open */ }
  };

  const savePw = async () => {
    setPwErr("");
    if (!newPw || newPw.length < 8) { setPwErr("Password must be at least 8 characters."); return; }
    setSavingPw(true);
    try {
      const r = await adminChangeStackPassword(cpInfo.hosting.id, newPw);
      if (!r.ok) {
        setPwErr(r.error || "Could not update password.");
      } else {
        onToast?.("Control panel password updated");
        setEditPw(false); setNewPw(""); setShowPw(true);
        await refreshCP();
      }
    } catch (e) {
      setPwErr(e?.response?.data?.detail || "Could not update password.");
    }
    setSavingPw(false);
  };

  const copyValue = async (key, value) => {
    try {
      await navigator.clipboard.writeText(value || "");
      setCopied(key);
      setTimeout(() => setCopied(""), 1500);
    } catch (e) { /* noop */ }
  };

  const refreshStackUser = async () => {
    if (!cpInfo?.hosting?.id) return;
    setRefreshingStack(true);
    try {
      const r = await adminRefreshStackUser(cpInfo.hosting.id);
      if (r.ok && r.refreshed) {
        onToast?.(`Linked ${r.stack_users.length} control panel user(s)`);
        await refreshCP();
        await load();
      } else {
        onToast?.(r.message || "No control panel user yet — try again in a few seconds", "error");
      }
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Refresh failed", "error");
    }
    setRefreshingStack(false);
  };

  const syncStackUserRow = async (h) => {
    setPerRowSyncing((s) => ({ ...s, [h.id]: true }));
    try {
      const r = await adminSyncStackUser(h.id);
      if (r.ok) {
        const action = r.action || "synced";
        onToast?.(`${h.domain_name}: ${action}${r.password_set ? " (new password saved)" : ""}`);
        await load();
      } else {
        onToast?.(r.reason || "Sync failed", "error");
      }
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Sync failed", "error");
    }
    setPerRowSyncing((s) => ({ ...s, [h.id]: false }));
  };

  const retrySupportUser = async (h) => {
    const key = `sup-${h.id}`;
    setPerRowSyncing((s) => ({ ...s, [key]: true }));
    try {
      const r = await adminRetrySupportUser(h.id);
      if (r.ok) {
        onToast?.(
          `${h.domain_name}: support user ${r.created ? "created" : "re-linked"}` +
          (r.grants_ok ? " · tickets+billing granted" : " · grants pending"),
        );
        await load();
      } else {
        const reason = typeof r.reason === "string" ? r.reason : JSON.stringify(r.reason);
        onToast?.(`Failed: ${reason}`.slice(0, 200), "error");
      }
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Retry failed", "error");
    }
    setPerRowSyncing((s) => ({ ...s, [key]: false }));
  };

  const openSupportCreds = async (h) => {
    try {
      const r = await adminGetSupportCredentials(h.id);
      setSupportCreds({
        hosting: h,
        email: r.support_email || "",
        password: r.support_password || "",
        has_support_credentials: r.has_support_credentials,
      });
      setShowSupportPw(false);
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Failed to load support credentials", "error");
    }
  };

  const saveSupportCreds = async () => {
    if (!supportCreds) return;
    const { hosting, email, password } = supportCreds;
    if ((email && !password) || (!email && password)) {
      onToast?.("Provide BOTH email and password — or leave both blank to clear", "error");
      return;
    }
    setSavingSupport(true);
    try {
      await adminSetSupportCredentials(hosting.id, { email: email || "", password: password || "" });
      onToast?.(email ? `Support credentials saved for ${hosting.domain_name}` : `Support credentials cleared for ${hosting.domain_name}`);
      setSupportCreds(null);
      await load();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Failed to save support credentials", "error");
    }
    setSavingSupport(false);
  };


  const bulkCreateSupportUsers = async () => {
    const ok = window.confirm(
      "Auto-create Support / Ticket StackCP users for hosting rows that don't have one yet?\n\n" +
      "• Uses each customer's email to create a basket-summary-login user on 20i.\n" +
      "• If the email already exists in 20i, the existing user is granted access to this hosting + a fresh password is set.\n" +
      "• 20i will send a welcome email to each new user.\n\n" +
      "Continue?"
    );
    if (!ok) return;
    setBulkCreatingSupport(true);
    try {
      const r = await adminBulkCreateSupportUsers();
      const s = r.summary || {};
      const msg =
        `Processed ${r.total} rows — ${s.created || 0} created, ${s.linked_existing || 0} linked, ` +
        `${s.skipped_existing_creds || 0} already had creds, ` +
        `${(s.skipped_no_email || 0) + (s.skipped_no_pkg || 0)} skipped, ${s.failed || 0} failed`;
      onToast?.(msg);
      await load();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Bulk support-user creation failed", "error");
    }
    setBulkCreatingSupport(false);
  };


  const bulkSyncStackUsers = async () => {
    const ok = window.confirm(
      "Sync StackCP users for all hosting rows?\n\n" +
      "• Rows with an existing 20i stack user → link + update contact email + reset password\n" +
      "• Rows without a stack user → create a new one (with the admin email) and assign\n\n" +
      "This may take a minute. Continue?"
    );
    if (!ok) return;
    setBulkSyncing(true);
    try {
      const r = await adminBulkSyncStackUsers();
      const s = r.summary || {};
      const msg =
        `Synced ${r.total} rows — ` +
        `${(s.linked_and_updated || 0)} linked, ` +
        `${(s.created || 0) + (s.created_pending_link || 0)} created, ` +
        `${s.skipped || 0} skipped, ${s.failed || 0} failed`;
      onToast?.(msg);
      await load();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Bulk sync failed", "error");
    }
    setBulkSyncing(false);
  };

  const openExpiryEdit = (h) => {
    const cur = h.expires_at ? new Date(h.expires_at) : new Date();
    // Format yyyy-mm-dd for <input type="date">
    const iso = cur.toISOString().slice(0, 10);
    setExpiryEdit({ hosting: h, value: iso });
  };

  const saveExpiry = async () => {
    if (!expiryEdit) return;
    setExpirySaving(true);
    try {
      // Send ISO datetime so the backend datetime field accepts it
      const iso = new Date(expiryEdit.value + "T00:00:00.000Z").toISOString();
      await adminUpdateHosting(expiryEdit.hosting.id, { expires_at: iso });
      onToast?.(`Expiry updated to ${expiryEdit.value}`);
      setExpiryEdit(null);
      await load();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Could not update expiry", "error");
    }
    setExpirySaving(false);
  };

  const openLockEdit = (h) => {
    setLockEdit({
      hosting: h,
      value: h.locked_renewal_price ? String(h.locked_renewal_price) : "",
      applyAll: false,
    });
  };

  const saveLocked = async () => {
    if (!lockEdit) return;
    setLockSaving(true);
    try {
      const raw = (lockEdit.value || "").toString().trim();
      const num = raw === "" ? null : Number(raw);
      if (num !== null && (isNaN(num) || num < 0)) {
        onToast?.("Enter a valid amount (or leave empty to clear)", "error");
        setLockSaving(false);
        return;
      }
      if (lockEdit.applyAll && lockEdit.hosting?.customer_id) {
        const r = await adminBulkLockRenewalForCustomer(lockEdit.hosting.customer_id, num);
        onToast?.(`Applied to ${r.modified} hosting(s) of this customer`);
      } else {
        await adminSetLockedRenewalPrice(lockEdit.hosting.id, num);
        onToast?.(num === null ? "Locked price cleared" : `Locked renewal price set to ₹${num}`);
      }
      setLockEdit(null);
      await load();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Could not save locked price", "error");
    }
    setLockSaving(false);
  };

  const oneClickLogin = async () => {
    if (!cpInfo?.hosting?.id) return;
    setSsoLoading(true);
    try {
      const r = await adminHostingSso(cpInfo.hosting.id);
      if (r.ok && r.url) {
        window.open(r.url, "_blank", "noopener");
      } else if (r.fallback === "send_welcome") {
        // Hosting platform does not expose SSO; fall back to sending the welcome/reset email
        const ok = window.confirm(
          (r.error || "Direct login not available.") +
          "\n\nSend the customer a welcome email with a password-setup link instead?"
        );
        if (ok) await sendWelcome(cpInfo.hosting);
      } else {
        onToast?.(r.error || "Could not generate login link", "error");
      }
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "SSO failed", "error");
    }
    setSsoLoading(false);
  };

  const sendWelcome = async (h) => {
    if (!window.confirm(`Send welcome email to all account users of ${h.domain_name}?`)) return;
    try {
      const res = await adminSendWelcome(h.id);
      if (res.ok) {
        onToast?.("Welcome email sent");
      } else {
        onToast?.("Platform returned: " + JSON.stringify(res.twentyi?.data || res.twentyi).slice(0, 100), "error");
      }
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Failed to send welcome email", "error");
    }
  };

  const emailCredentials = async () => {
    if (!cpInfo?.hosting?.id) return;
    const to = window.prompt(
      `Email full control panel credentials to:`,
      cpInfo.hosting.customer_email || "",
    );
    if (!to) return;
    setEmailingCreds(true);
    try {
      const res = await adminEmailCredentials(cpInfo.hosting.id, to);
      onToast?.(`Credentials emailed to ${res.sent_to}`);
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Failed to email credentials", "error");
    }
    setEmailingCreds(false);
  };

  const loadDuplicates = async () => {
    setDupLoading(true);
    try {
      const res = await adminHostingDuplicates();
      const groups = res.groups || [];
      // Auto-pick the row WITH a 20i package id as default keeper (the real linked one)
      const initial = {};
      groups.forEach((g) => {
        const linked = (g.items || []).find((i) => i.twentyi_package_id);
        initial[g.domain] = (linked || g.items[0])?.id;
      });
      setDupGroups(groups);
      setDupKeeper(initial);
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Failed to load duplicates", "error");
    }
    setDupLoading(false);
  };

  const openDuplicates = async () => {
    setDupOpen(true);
    await loadDuplicates();
  };

  const mergeGroup = async (group) => {
    const keeperId = dupKeeper[group.domain];
    if (!keeperId) {
      onToast?.("Pick which row to keep first", "error");
      return;
    }
    const dropIds = (group.items || []).filter((i) => i.id !== keeperId).map((i) => i.id);
    if (!dropIds.length) return;
    if (!window.confirm(
      `Merge ${dropIds.length + 1} records for ${group.domain} into one?\n\n` +
      `Kept row will absorb the 20i package id + stack user from the dropped rows. ` +
      `Previous customer emails are added as shared users so they keep portal access. ` +
      `The 20i package itself is NOT touched.`
    )) return;
    try {
      await adminMergeDuplicates(keeperId, dropIds);
      onToast?.(`Merged ${group.domain} (${dropIds.length} dropped)`);
      await loadDuplicates();
      await load();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Merge failed", "error");
    }
  };

  const save = async () => {
    try {
      const payload = { ...form, amount_inr: form.amount_inr ? Number(form.amount_inr) : null };
      await adminCreateHosting(payload);
      onToast?.("Hosting account created");
      setCreating(false);
      setForm(blank);
      await load();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Create failed", "error");
    }
  };

  const suspend = async (id) => {
    if (!window.confirm("Suspend this hosting account?")) return;
    await adminSuspendHosting(id);
    onToast?.("Suspended");
    await load();
  };
  const unsuspend = async (id) => {
    await adminUnsuspendHosting(id);
    onToast?.("Unsuspended");
    await load();
  };
  const renew = async () => {
    try {
      await adminRenewHosting({ hosting_id: renewing.id, months: Number(renewMonths) });
      onToast?.(`Renewed ${renewMonths} months · invoice generated`);
      setRenewing(null);
      await load();
    } catch (e) {
      onToast?.("Renew failed", "error");
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this hosting record only (keep the package on the platform untouched)?")) return;
    await adminDeleteHosting(id);
    onToast?.("Local record deleted");
    await load();
  };

  const openShare = (h) => {
    setSharing(h);
    setShareText((h.shared_with_emails || []).join(", "));
  };

  const saveShare = async () => {
    if (!sharing) return;
    setShareSaving(true);
    try {
      const emails = shareText.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
      await adminUpdateHostingShare(sharing.id, emails);
      onToast?.(`Updated shared users for ${sharing.domain_name}`);
      setSharing(null);
      setShareText("");
      await load();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Failed to update shared users", "error");
    }
    setShareSaving(false);
  };

  const runConsolidate = async () => {
    if (!consForm.target_email) { onToast?.("Target email is required", "error"); return; }
    if (!window.confirm(
      `Merge ALL hosting records under "${consForm.target_email}"?\n\n` +
      `• Every hosting will get this user as primary owner.\n` +
      `• Previous owners are kept as shared users so they can still access their own hosting.\n` +
      `• Orphan placeholder customers will be cleaned up.\n\nProceed?`
    )) return;
    setConsBusy(true);
    try {
      const res = await adminConsolidateHosting(consForm);
      let msg = `Moved ${res.moved} hosting → ${res.target_email}. Login user: ${res.login_user}.`;
      if (res.temp_password) msg += ` Temp password: ${res.temp_password}`;
      if (res.orphan_customers_deleted) msg += ` Cleaned ${res.orphan_customers_deleted} orphan customers.`;
      onToast?.(msg);
      setConsolidating(false);
      await load();
      await loadCustomers();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Consolidation failed", "error");
    }
    setConsBusy(false);
  };


  const unlink = async (h) => {
    if (!window.confirm(
      `Unlink "${h.domain_name}"?\n\n` +
      `• The package will be SUSPENDED on 20i (customer can't access).\n` +
      `• The hosting will be HIDDEN from the customer's portal.\n` +
      `• The row STAYS in the admin panel so you can re-link later.\n` +
      `• Files & databases on 20i are untouched.`
    )) return;
    try {
      await adminUnlinkHosting(h.id);
      onToast?.("Unlinked + suspended on 20i. Hidden from customer portal.");
      await load();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Unlink failed", "error");
    }
  };

  const relink = async (h) => {
    if (!window.confirm(
      `Re-link "${h.domain_name}"?\n\n` +
      `• The package will be RE-ENABLED on 20i.\n` +
      `• The hosting will become VISIBLE again in the customer portal.`
    )) return;
    try {
      await adminRelinkHosting(h.id);
      onToast?.("Re-linked + activated on 20i. Customer can access again.");
      await load();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Re-link failed", "error");
    }
  };

  const cascadeDelete = async (h) => {
    const typed = window.prompt(
      `⚠️ DESTRUCTIVE — this PERMANENTLY cancels "${h.domain_name}" on the platform.\n` +
      `All files, databases and emails will be deleted irreversibly.\n\n` +
      `Type the domain name exactly to confirm:`
    );
    if (typed !== h.domain_name) {
      if (typed !== null) onToast?.("Domain didn't match — cancelled", "error");
      return;
    }
    try {
      await adminDeleteHostingCascade(h.id);
      onToast?.("Package permanently deleted on platform");
      await load();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Delete failed", "error");
    }
  };

  const statusBadge = (s) => {
    const map = {
      active: "bg-green-100 text-green-700",
      suspended: "bg-yellow-100 text-yellow-700",
      provision_failed: "bg-red-100 text-red-700",
      expired: "bg-gray-100 text-gray-700",
    };
    return `text-xs font-bold px-2 py-1 rounded ${map[s] || "bg-gray-100 text-gray-700"}`;
  };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="lm-font font-extrabold text-[#11151C] text-2xl">Hosting Accounts</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={openDuplicates}
            data-testid="hosting-find-duplicates"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold border border-[#11151C]/15 bg-white hover:bg-[#FFE6D6]/40"
            title="Find hosting rows that share the same domain"
          >
            <AlertTriangle className="w-4 h-4 text-[#FF6B3D]" /> Find Duplicates
          </button>
          <button
            onClick={() => setConsolidating(true)}
            data-testid="hosting-consolidate-button"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold border border-[#11151C]/15 bg-white hover:bg-[#FFE6D6]/40"
            title="Merge multiple hosting records under one primary customer"
          >
            <Merge className="w-4 h-4 text-[#11151C]" /> Consolidate
          </button>
          <button
            onClick={syncWithPrompt}
            disabled={syncing}
            data-testid="hosting-sync-platform"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold bg-[#FFE6D6] text-[#B23B12] hover:bg-[#FFD0B8] disabled:opacity-50"
          >
            <Download className={`w-4 h-4 ${syncing ? "animate-pulse" : ""}`} />
            {syncing ? "Syncing..." : "Sync Platform Accounts"}
          </button>
          <button
            onClick={bulkSyncStackUsers}
            disabled={bulkSyncing}
            data-testid="hosting-bulk-sync-stackusers"
            title="Link/create StackCP users for every hosting row"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold border border-[#FF6B3D]/40 bg-white text-[#B23B12] hover:bg-[#FFE6D6] disabled:opacity-50"
          >
            <UserIcon className={`w-4 h-4 ${bulkSyncing ? "animate-pulse" : ""}`} />
            {bulkSyncing ? "Syncing StackCP..." : "Sync StackCP Users"}
          </button>
          <button
            onClick={bulkCreateSupportUsers}
            disabled={bulkCreatingSupport}
            data-testid="hosting-bulk-create-support-users"
            title="Auto-create basket-summary-login users for every hosting row that's missing support credentials"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold border border-[#FF6B3D]/40 bg-white text-[#B23B12] hover:bg-[#FFE6D6] disabled:opacity-50"
          >
            <Mail className={`w-4 h-4 ${bulkCreatingSupport ? "animate-pulse" : ""}`} />
            {bulkCreatingSupport ? "Creating support users..." : "Auto-create Support Users"}
          </button>
          <button onClick={() => setCreating(true)} className="lm-btn-primary inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold">
            <Plus className="w-4 h-4" /> Provision Hosting
          </button>
        </div>
      </div>

      {creating && (
        <div className="mt-5 border border-[#11151C]/10 rounded-2xl p-5">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-[#11151C]">New hosting account</h3>
            <button onClick={() => setCreating(false)}><X className="w-4 h-4" /></button>
          </div>
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-semibold text-[#11151C]/70 uppercase tracking-wider">Customer</span>
              <select value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })}
                className="mt-1 w-full border border-[#11151C]/15 rounded-xl px-3 py-2 text-sm">
                <option value="">Select customer…</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.email})</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-[#11151C]/70 uppercase tracking-wider">Plan</span>
              <select value={form.plan_key} onChange={(e) => setForm({ ...form, plan_key: e.target.value })}
                className="mt-1 w-full border border-[#11151C]/15 rounded-xl px-3 py-2 text-sm">
                <option value="">Select plan…</option>
                {plans.map((p) => <option key={p.key} value={p.key}>{p.name} — ₹{p.price}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-[#11151C]/70 uppercase tracking-wider">Domain</span>
              <input value={form.domain_name} onChange={(e) => setForm({ ...form, domain_name: e.target.value })}
                placeholder="example.com"
                className="mt-1 w-full border border-[#11151C]/15 rounded-xl px-3 py-2 text-sm" />
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-[#11151C]/70 uppercase tracking-wider">Billing</span>
              <select value={form.billing_cycle} onChange={(e) => setForm({ ...form, billing_cycle: e.target.value })}
                className="mt-1 w-full border border-[#11151C]/15 rounded-xl px-3 py-2 text-sm">
                <option value="annual">Annual (12 months)</option>
                <option value="monthly">Monthly (1 month)</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-[#11151C]/70 uppercase tracking-wider">Override Amount (₹)</span>
              <input type="number" value={form.amount_inr} onChange={(e) => setForm({ ...form, amount_inr: e.target.value })}
                placeholder="Leave blank to use plan price"
                className="mt-1 w-full border border-[#11151C]/15 rounded-xl px-3 py-2 text-sm" />
            </label>
            <label className="flex items-center gap-2 mt-6">
              <input type="checkbox" checked={form.provision_now} onChange={(e) => setForm({ ...form, provision_now: e.target.checked })} />
              <span className="text-sm font-semibold">Provision now</span>
            </label>
            <div className="sm:col-span-2">
              <label className="block">
                <span className="text-xs font-semibold text-[#11151C]/70 uppercase tracking-wider">Notes</span>
                <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="mt-1 w-full border border-[#11151C]/15 rounded-xl px-3 py-2 text-sm" />
              </label>
            </div>
          </div>
          <button onClick={save} className="lm-btn-primary mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold">
            <Save className="w-4 h-4" /> Create
          </button>
        </div>
      )}

      <div className="mt-6 border border-[#11151C]/10 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FFFBF6]">
            <tr>
              <th className="text-left p-3">Domain</th>
              <th className="text-left p-3">Customer</th>
              <th className="text-left p-3">Plan</th>
              <th className="text-left p-3">Expires</th>
              <th className="text-left p-3">Status</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-[#11151C]/50">No hosting accounts yet.</td></tr>
            )}
            {list.map((h) => (
              <tr key={h.id} className="border-t border-[#11151C]/5">
                <td className="p-3 font-semibold flex items-center gap-1.5"><Globe className="w-3.5 h-3.5 text-[#11151C]/40" /> {h.domain_name}</td>
                <td className="p-3 text-xs">
                  {h.customer_name}<br />
                  <span className="text-[#11151C]/50">{h.customer_email}</span>
                  {(h.shared_with_emails && h.shared_with_emails.length > 0) && (
                    <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#B23B12] bg-[#FFE6D6] px-1.5 py-0.5 rounded">
                      <Users className="w-3 h-3" /> +{h.shared_with_emails.length} shared
                    </div>
                  )}
                </td>
                <td className="p-3 text-xs">{h.plan_name}<br /><span className="text-[#11151C]/50">₹{h.amount_inr} / {h.billing_cycle}</span>
                  {h.locked_renewal_price ? (
                    <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#B23B12] bg-[#FFE6D6] px-1.5 py-0.5 rounded" title="Locked renewal price">
                      <BadgePercent className="w-3 h-3" /> renews ₹{h.locked_renewal_price}
                    </div>
                  ) : null}
                </td>
                <td className="p-3 text-xs">
                  {h.expires_at ? new Date(h.expires_at).toLocaleDateString() : "—"}
                  <button
                    onClick={() => openExpiryEdit(h)}
                    data-testid={`hosting-edit-expiry-${h.id}`}
                    title="Edit expiry date"
                    className="ml-1.5 p-1 hover:bg-[#FFE6D6] rounded inline-flex align-middle"
                  >
                    <Calendar className="w-3.5 h-3.5 text-[#FF6B3D]" />
                  </button>
                </td>
                <td className="p-3"><span className={statusBadge(h.status)}>{h.status}</span></td>
                <td className="p-3 text-right whitespace-nowrap">
                  <button onClick={() => openCP(h)} title="Open Control Panel" className="p-1.5 hover:bg-[#FFE6D6] rounded-lg"><ExternalLink className="w-4 h-4 text-[#11151C]" /></button>
                  {h.twentyi_package_id && (
                    <button onClick={() => sendWelcome(h)} title="Send Welcome Email" className="p-1.5 hover:bg-blue-50 rounded-lg"><Mail className="w-4 h-4 text-blue-600" /></button>
                  )}
                  <button onClick={() => openShare(h)} title="Manage shared users" data-testid={`hosting-share-${h.id}`} className="p-1.5 hover:bg-[#FFE6D6] rounded-lg"><Users className="w-4 h-4 text-[#11151C]" /></button>
                  {h.status === "active" ? (
                    <button onClick={() => suspend(h.id)} title="Suspend" className="p-1.5 hover:bg-yellow-50 rounded-lg"><PauseCircle className="w-4 h-4 text-yellow-600" /></button>
                  ) : (
                    <button onClick={() => unsuspend(h.id)} title="Activate" className="p-1.5 hover:bg-green-50 rounded-lg"><PlayCircle className="w-4 h-4 text-green-600" /></button>
                  )}
                  <button onClick={() => { setRenewing(h); setRenewMonths(12); }} title="Renew" className="p-1.5 hover:bg-[#FFE6D6] rounded-lg"><RefreshCw className="w-4 h-4 text-[#FF6B3D]" /></button>
                  <button
                    onClick={() => openLockEdit(h)}
                    data-testid={`hosting-lock-price-${h.id}`}
                    title={h.locked_renewal_price ? `Locked renewal: ₹${h.locked_renewal_price} — click to edit` : "Set locked renewal price"}
                    className={`p-1.5 rounded-lg ${h.locked_renewal_price ? "bg-[#FFE6D6] hover:bg-[#FFD2B0]" : "hover:bg-[#FFE6D6]"}`}
                  >
                    <BadgePercent className={`w-4 h-4 ${h.locked_renewal_price ? "text-[#B23B12]" : "text-[#11151C]/45"}`} />
                  </button>
                  <button onClick={() => syncStackUserRow(h)} disabled={!!perRowSyncing[h.id]} title="Sync / create StackCP user" data-testid={`hosting-sync-stack-${h.id}`} className="p-1.5 hover:bg-[#FFE6D6] rounded-lg disabled:opacity-50">
                    {perRowSyncing[h.id] ? <Loader2 className="w-4 h-4 animate-spin text-[#B23B12]" /> : <UserIcon className="w-4 h-4 text-[#B23B12]" />}
                  </button>
                  <button
                    onClick={() => openSupportCreds(h)}
                    title={h.has_support_credentials ? "Edit Support / Ticket login (saved)" : "Add Support / Ticket login"}
                    data-testid={`hosting-support-creds-${h.id}`}
                    className={`p-1.5 rounded-lg ${h.has_support_credentials ? "hover:bg-green-50" : "hover:bg-[#FFE6D6]"}`}
                  >
                    <Lock className={`w-4 h-4 ${h.has_support_credentials ? "text-green-600" : "text-[#11151C]/45"}`} />
                  </button>
                  <button
                    onClick={() => retrySupportUser(h)}
                    disabled={!!perRowSyncing[`sup-${h.id}`]}
                    title="Auto-create / reset 20i Support StackCP user (sets new password & grants tickets+billing+package)"
                    data-testid={`hosting-retry-support-${h.id}`}
                    className="p-1.5 hover:bg-[#FFE6D6] rounded-lg disabled:opacity-50"
                  >
                    {perRowSyncing[`sup-${h.id}`]
                      ? <Loader2 className="w-4 h-4 animate-spin text-[#FF6B3D]" />
                      : <RotateCw className="w-4 h-4 text-[#FF6B3D]" />}
                  </button>
                  <button onClick={() => unlink(h)} title="Unlink (suspend on platform)" data-testid={`hosting-unlink-${h.id}`} className="p-1.5 hover:bg-yellow-50 rounded-lg"><Link2Off className="w-4 h-4 text-yellow-600" /></button>
                  <button onClick={() => cascadeDelete(h)} title="Cancel package on Platform (destructive)" data-testid={`hosting-cascade-delete-${h.id}`} className="p-1.5 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4 text-red-500" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {supportCreds && (
        <div className="fixed inset-0 z-50 bg-black/55 flex items-center justify-center p-4" onClick={() => setSupportCreds(null)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="admin-support-creds-modal">
            <div className="flex items-center justify-between mb-1">
              <h3 className="lm-font font-extrabold text-[#11151C] text-lg inline-flex items-center gap-2">
                <Lock className="w-5 h-5 text-[#FF6B3D]" /> Support / Ticket login
              </h3>
              <button onClick={() => setSupportCreds(null)} className="text-[#11151C]/50"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-[#11151C]/65 mt-1">{supportCreds.hosting?.domain_name}</p>
            <p className="text-xs text-[#11151C]/55 mt-2">
              Manually enter the 20i basket-summary credentials you created in the 20i panel. The customer will see these in their portal&apos;s Support &amp; Tickets popup.
            </p>

            <div className="mt-4 space-y-3">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-[#11151C]/70">
                  Customer&apos;s 20i login email
                </label>
                <input
                  type="email"
                  value={supportCreds.email}
                  onChange={(e) => setSupportCreds((s) => ({ ...s, email: e.target.value }))}
                  placeholder="customer@example.com"
                  data-testid="support-creds-email"
                  className="mt-1 w-full border border-[#11151C]/15 rounded-lg px-3 py-2 text-sm font-mono"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-[#11151C]/70">
                  Password
                </label>
                <div className="mt-1 flex items-center gap-2">
                  <input
                    type={showSupportPw ? "text" : "password"}
                    value={supportCreds.password}
                    onChange={(e) => setSupportCreds((s) => ({ ...s, password: e.target.value }))}
                    placeholder="Enter the StackCP password"
                    data-testid="support-creds-password"
                    className="flex-1 border border-[#11151C]/15 rounded-lg px-3 py-2 text-sm font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSupportPw(!showSupportPw)}
                    title={showSupportPw ? "Hide" : "Reveal"}
                    className="p-2 rounded-md hover:bg-[#FFE6D6] text-[#11151C]/70"
                  >
                    {showSupportPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <p className="text-[11px] text-[#11151C]/55">
                Leave BOTH blank and save to clear (hides the Support button from the customer).
              </p>
            </div>

            <div className="mt-5 flex gap-2 justify-end">
              <button
                onClick={() => setSupportCreds(null)}
                className="px-4 py-2 text-sm font-semibold text-[#11151C]"
              >
                Cancel
              </button>
              <button
                onClick={saveSupportCreds}
                disabled={savingSupport}
                data-testid="support-creds-save"
                className="lm-btn-primary inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold disabled:opacity-60"
              >
                {savingSupport ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save
              </button>
            </div>
          </div>
        </div>
      )}


      {renewing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setRenewing(null)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="lm-font font-extrabold text-[#11151C] text-lg">Renew hosting</h3>
            <p className="text-sm text-[#11151C]/60 mt-1">{renewing.domain_name} · {renewing.plan_name}</p>

            <div className="mt-4">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#11151C]/70">Quick select</span>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {[
                  { label: "1 Year", m: 12 },
                  { label: "2 Years", m: 24 },
                  { label: "3 Years", m: 36 },
                ].map((opt) => (
                  <button
                    key={opt.m}
                    type="button"
                    onClick={() => setRenewMonths(opt.m)}
                    data-testid={`renew-preset-${opt.m}`}
                    className={`py-2 rounded-xl text-sm font-bold border transition ${
                      Number(renewMonths) === opt.m
                        ? "border-[#FF6B3D] bg-[#FFE6D6] text-[#B23B12]"
                        : "border-[#11151C]/15 hover:bg-[#FFE6D6]/40 text-[#11151C]"
                    }`}
                  >{opt.label}</button>
                ))}
              </div>
            </div>

            <label className="block mt-4">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#11151C]/70">Or custom (months)</span>
              <input
                type="number"
                min={1}
                value={renewMonths}
                onChange={(e) => setRenewMonths(e.target.value)}
                data-testid="renew-months-input"
                className="mt-1 w-full border border-[#11151C]/15 rounded-xl px-3 py-2 text-sm"
              />
            </label>
            <p className="text-xs text-[#11151C]/50 mt-2">
              An unpaid invoice will be created for ₹{(Number(renewing.amount_inr) || 0) * (Number(renewMonths) / 12 || 1)}.
              If your 20i plan supports multi-year, the package term extends accordingly.
            </p>
            <div className="mt-4 flex gap-2 justify-end">
              <button onClick={() => setRenewing(null)} className="px-4 py-2 text-sm font-semibold" data-testid="renew-cancel">Cancel</button>
              <button onClick={renew} data-testid="renew-confirm" className="lm-btn-primary px-4 py-2.5 rounded-xl text-sm font-bold inline-flex items-center gap-2">
                <RefreshCw className="w-4 h-4 text-[#FF6B3D]" /> Renew {renewMonths} months
              </button>
            </div>
          </div>
        </div>
      )}

      {expiryEdit && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setExpiryEdit(null)}>
          <div className="bg-white rounded-2xl max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="expiry-edit-modal">
            <div className="flex items-center justify-between">
              <h3 className="lm-font font-extrabold text-[#11151C] text-lg inline-flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#FF6B3D]" /> Edit expiry date
              </h3>
              <button onClick={() => setExpiryEdit(null)}><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-[#11151C]/60 mt-1">{expiryEdit.hosting.domain_name}</p>
            <label className="block mt-4">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#11151C]/70">New expiry date</span>
              <input
                type="date"
                value={expiryEdit.value}
                onChange={(e) => setExpiryEdit({ ...expiryEdit, value: e.target.value })}
                data-testid="expiry-edit-input"
                className="mt-1 w-full border border-[#11151C]/15 rounded-xl px-3 py-2 text-sm"
              />
            </label>
            <p className="text-[11px] text-[#11151C]/55 mt-2">
              This only updates the locally-tracked expiry date (for dashboard reporting). It does not change the term on the hosting platform itself — use Renew for that.
            </p>
            <div className="mt-4 flex gap-2 justify-end">
              <button onClick={() => setExpiryEdit(null)} className="px-4 py-2 text-sm font-semibold">Cancel</button>
              <button
                onClick={saveExpiry}
                disabled={expirySaving}
                data-testid="expiry-edit-save"
                className="lm-btn-primary px-4 py-2.5 rounded-xl text-sm font-bold inline-flex items-center gap-2 disabled:opacity-60"
              >
                {expirySaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {lockEdit && (
        <div className="fixed inset-0 z-50 bg-black/55 flex items-center justify-center p-4" onClick={() => setLockEdit(null)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="locked-price-modal">
            <div className="flex items-center justify-between">
              <h3 className="lm-font font-extrabold text-[#11151C] text-lg inline-flex items-center gap-2">
                <BadgePercent className="w-5 h-5 text-[#FF6B3D]" /> Locked renewal price
              </h3>
              <button onClick={() => setLockEdit(null)}><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-[#11151C]/65 mt-1">{lockEdit.hosting.domain_name}</p>
            <p className="text-xs text-[#11151C]/55 mt-2">
              Customer-specific promo price that overrides the plan&apos;s 12-month renewal rate for this hosting. Leave blank to clear.
            </p>
            <label className="block mt-4">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#11151C]/70">Locked price (₹/year)</span>
              <input
                type="number"
                min="0"
                step="1"
                value={lockEdit.value}
                onChange={(e) => setLockEdit({ ...lockEdit, value: e.target.value })}
                placeholder="e.g. 1499 — or blank to clear"
                data-testid="locked-price-input"
                className="mt-1 w-full border border-[#11151C]/15 rounded-xl px-3 py-2 text-sm"
              />
            </label>
            <label className="mt-3 flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!lockEdit.applyAll}
                onChange={(e) => setLockEdit({ ...lockEdit, applyAll: e.target.checked })}
                data-testid="locked-price-apply-all"
                className="mt-0.5"
              />
              <span className="text-xs text-[#11151C]/75">
                Apply this lock to <b>all hostings</b> of <b>{lockEdit.hosting.customer_email || lockEdit.hosting.customer_name}</b>
              </span>
            </label>
            <div className="mt-4 flex gap-2 justify-end">
              <button onClick={() => setLockEdit(null)} className="px-4 py-2 text-sm font-semibold">Cancel</button>
              <button
                onClick={saveLocked}
                disabled={lockSaving}
                data-testid="locked-price-save"
                className="lm-btn-primary px-4 py-2.5 rounded-xl text-sm font-bold inline-flex items-center gap-2 disabled:opacity-60"
              >
                {lockSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {cpInfo && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setCpInfo(null)}>
          <div className="bg-white rounded-2xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="admin-cp-modal">
            <div className="flex items-center justify-between">
              <h3 className="lm-font font-extrabold text-[#11151C] text-lg">Control Panel Login</h3>
              <button onClick={() => setCpInfo(null)}><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-[#11151C]/65 mt-1">{cpInfo.hosting?.domain_name}</p>

            <div className="mt-4 p-4 rounded-xl bg-[#FFFBF6] border border-[#FF6B3D]/20 space-y-3">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#11151C]/60 inline-flex items-center gap-1">
                  <Lock className="w-3 h-3" /> Login URL
                </div>
                <div className="mt-1 flex items-center justify-between gap-2">
                  <a
                    href={cpInfo.control_panel_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[#FF6B3D] font-semibold break-all"
                  >
                    {cpInfo.control_panel_url} <ExternalLink className="w-3 h-3" />
                  </a>
                  <button onClick={() => copyValue("url", cpInfo.control_panel_url)} title="Copy URL" data-testid="admin-cp-copy-url" className="p-1.5 rounded-md hover:bg-[#FFE6D6] text-[#11151C]/70">
                    {copied === "url" ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>

              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#11151C]/60 inline-flex items-center gap-1">
                  <UserIcon className="w-3 h-3" /> Username
                </div>
                <div className="mt-1 flex items-center justify-between gap-2">
                  <code className="font-mono text-sm text-[#11151C] break-all" data-testid="admin-cp-username">{cpInfo.username || "—"}</code>
                  <button onClick={() => copyValue("user", cpInfo.username)} disabled={!cpInfo.username} title="Copy username" data-testid="admin-cp-copy-username" className="p-1.5 rounded-md hover:bg-[#FFE6D6] text-[#11151C]/70 disabled:opacity-30">
                    {copied === "user" ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>

              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#11151C]/60 inline-flex items-center gap-1">
                  <KeyRound className="w-3 h-3" /> Password
                </div>
                {cpInfo.has_password ? (
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <code className="font-mono text-sm text-[#11151C] break-all" data-testid="admin-cp-password">
                      {showPw ? cpInfo.password : "•".repeat(Math.max(8, (cpInfo.password || "").length))}
                    </code>
                    <div className="flex items-center">
                      <button onClick={() => setShowPw(!showPw)} title={showPw ? "Hide" : "Reveal"} data-testid="admin-cp-toggle-show" className="p-1.5 rounded-md hover:bg-[#FFE6D6] text-[#11151C]/70">
                        {showPw ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                      </button>
                      <button onClick={() => copyValue("pw", cpInfo.password)} title="Copy password" data-testid="admin-cp-copy-password" className="ml-1 p-1.5 rounded-md hover:bg-[#FFE6D6] text-[#11151C]/70">
                        {copied === "pw" ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="mt-1 text-xs text-[#11151C]/60 italic">
                    No password saved. Click &quot;Set Password&quot; below or email the customer a welcome link.
                  </p>
                )}
              </div>
            </div>

            {/* Change/Set password */}
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={refreshStackUser}
                disabled={refreshingStack}
                data-testid="admin-cp-refresh-stack"
                title="Re-fetch the control panel user from the platform (use after a fresh purchase)"
                className="inline-flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-bold border border-[#11151C]/15 hover:bg-[#FFE6D6]/40 disabled:opacity-60"
              >
                {refreshingStack ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCw className="w-4 h-4 text-[#FF6B3D]" />}
                Refresh from Platform
              </button>
              {!editPw && (
                <button
                  type="button"
                  onClick={() => { setEditPw(true); setPwErr(""); }}
                  data-testid="admin-cp-edit-password"
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold border border-[#FF6B3D]/40 bg-[#FFFBF6] text-[#B23B12] hover:bg-[#FFE6D6]"
                >
                  <KeyRound className="w-4 h-4" />
                  {cpInfo.has_password ? "Change Password" : "Set Password"}
                </button>
              )}
            </div>
            {editPw && (
              <div className="mt-3 border border-[#FF6B3D]/30 rounded-xl p-3 bg-[#FFFBF6]">
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-[#11151C]/70">New password (8+ chars)</label>
                  <input
                    type="text"
                    value={newPw}
                    onChange={(e) => setNewPw(e.target.value)}
                    placeholder="Enter a strong password"
                    autoComplete="new-password"
                    data-testid="admin-cp-new-password-input"
                    className="mt-1 w-full border border-[#11151C]/15 rounded-lg px-3 py-2 text-sm font-mono"
                  />
                  {pwErr && (
                    <p className="text-xs text-red-600 mt-1.5 inline-flex items-center gap-1">
                      <AlertTriangle className="w-3.5 h-3.5" /> {pwErr}
                    </p>
                  )}
                  <div className="mt-2 flex gap-2">
                    <button
                      type="button"
                      onClick={savePw}
                      disabled={savingPw}
                      data-testid="admin-cp-save-password"
                      className="lm-btn-primary flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-bold disabled:opacity-60"
                    >
                      {savingPw ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={() => { setEditPw(false); setNewPw(""); setPwErr(""); }}
                      className="px-3 py-2 text-sm font-semibold text-[#11151C]"
                    >Cancel</button>
                  </div>
                  <p className="text-[11px] text-[#11151C]/55 mt-2">
                    This updates the hosting control panel password instantly via the platform API.
                  </p>
                </div>
              )}

            <a
              href={cpInfo.control_panel_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="admin-cp-open-page"
              className="mt-4 lm-btn-primary w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold"
            >
              Open Login Page <ExternalLink className="w-4 h-4" />
            </a>

            <div className="mt-4 pt-4 border-t border-[#11151C]/10">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#11151C]/60 mb-2 inline-flex items-center gap-1.5">
                <Mail className="w-3 h-3" /> Email these credentials directly to the customer
              </div>
              <button
                onClick={emailCredentials}
                disabled={emailingCreds || !cpInfo.has_password || !cpInfo.username}
                data-testid="cp-email-credentials"
                className="lm-btn-primary w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold disabled:opacity-50"
              >
                {emailingCreds ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                Email URL + username + password (via SES)
              </button>
              <p className="text-[11px] text-[#11151C]/50 mt-2">
                Sends a branded email bundling the control-panel URL, username, and current password in one go. Requires a password to be set above.
              </p>
            </div>

            <div className="mt-4 pt-4 border-t border-[#11151C]/10">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#11151C]/60 mb-2 inline-flex items-center gap-1.5">
                <Mail className="w-3 h-3" /> Or send the platform&apos;s native welcome email
              </div>
              <button
                onClick={() => sendWelcome(cpInfo.hosting)}
                data-testid="cp-send-welcome"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-xs font-bold border border-[#11151C]/15 hover:bg-[#FFE6D6]/40"
              >
                <Mail className="w-3.5 h-3.5" /> Send password-setup email
              </button>
              <p className="text-[11px] text-[#11151C]/50 mt-2">
                Sends a secure setup link to the customer&apos;s contact address (useful if they forgot their password).
              </p>
            </div>

            <div className="mt-4 flex justify-end">
              <button onClick={() => setCpInfo(null)} className="px-4 py-2 text-sm font-semibold text-[#11151C]" data-testid="admin-cp-close">Close</button>
            </div>
          </div>
        </div>
      )}
      {sharing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setSharing(null)}>
          <div className="bg-white rounded-2xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="lm-font font-extrabold text-[#11151C] text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-[#FF6B3D]" /> Shared access
              </h3>
              <button onClick={() => setSharing(null)} data-testid="share-modal-close"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-[#11151C]/65 mt-1">{sharing.domain_name}</p>
            <div className="mt-3 p-3 rounded-xl bg-[#FFFBF6] border border-[#FF6B3D]/20 text-xs">
              <div className="font-bold text-[#11151C]/60 uppercase tracking-wider mb-1">Primary owner</div>
              <div className="text-[#11151C] font-semibold break-all">{sharing.customer_email}</div>
            </div>
            <label className="block mt-4">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#11151C]/70">Additional users with access (one email per line or comma-separated)</span>
              <textarea
                rows={5}
                value={shareText}
                onChange={(e) => setShareText(e.target.value)}
                placeholder="user1@example.com, user2@example.com"
                data-testid="share-emails-textarea"
                className="mt-1 w-full border border-[#11151C]/15 rounded-xl px-3 py-2 text-sm font-mono"
              />
            </label>
            <p className="text-[11px] text-[#11151C]/50 mt-2">
              Each user must already exist as a Website User (or be created later with the same email). They will see this hosting in their customer portal.
            </p>
            <div className="mt-4 flex gap-2 justify-end">
              <button onClick={() => setSharing(null)} className="px-4 py-2 text-sm font-semibold">Cancel</button>
              <button
                onClick={saveShare}
                disabled={shareSaving}
                data-testid="share-save-button"
                className="lm-btn-primary px-4 py-2.5 rounded-xl text-sm font-bold inline-flex items-center gap-2 disabled:opacity-60"
              >
                {shareSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save shared users
              </button>
            </div>
          </div>
        </div>
      )}

      {consolidating && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setConsolidating(false)}>
          <div className="bg-white rounded-2xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="lm-font font-extrabold text-[#11151C] text-lg flex items-center gap-2">
                <Merge className="w-5 h-5 text-[#FF6B3D]" /> Consolidate hosting
              </h3>
              <button onClick={() => setConsolidating(false)} data-testid="consolidate-modal-close"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-[#11151C]/65 mt-1">
              Merge every hosting record under one primary customer. Previous owners are kept as <strong>shared users</strong> so they can still access their own package.
            </p>
            <div className="mt-4 grid gap-3">
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wider text-[#11151C]/70">Primary email *</span>
                <input
                  type="text"
                  value={consForm.target_email}
                  onChange={(e) => setConsForm({ ...consForm, target_email: e.target.value })}
                  placeholder="customer@example.com"
                  data-testid="consolidate-target-email"
                  className="mt-1 w-full border border-[#11151C]/15 rounded-xl px-3 py-2 text-sm"
                />
              </label>
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wider text-[#11151C]/70">Display name</span>
                <input
                  type="text"
                  value={consForm.target_name}
                  onChange={(e) => setConsForm({ ...consForm, target_name: e.target.value })}
                  placeholder="Customer Name"
                  data-testid="consolidate-target-name"
                  className="mt-1 w-full border border-[#11151C]/15 rounded-xl px-3 py-2 text-sm"
                />
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={consForm.create_login_user}
                  onChange={(e) => setConsForm({ ...consForm, create_login_user: e.target.checked })}
                  data-testid="consolidate-create-login"
                />
                <span>Create a Website User login for this email (if missing)</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={consForm.keep_previous_as_shared}
                  onChange={(e) => setConsForm({ ...consForm, keep_previous_as_shared: e.target.checked })}
                  data-testid="consolidate-keep-shared"
                />
                <span>Keep previous owners as shared users (recommended)</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={consForm.cleanup_orphan_customers}
                  onChange={(e) => setConsForm({ ...consForm, cleanup_orphan_customers: e.target.checked })}
                  data-testid="consolidate-cleanup"
                />
                <span>Delete leftover placeholder customers (<code>*@imported.livemover.local</code>)</span>
              </label>
            </div>
            <div className="mt-5 flex gap-2 justify-end">
              <button onClick={() => setConsolidating(false)} className="px-4 py-2 text-sm font-semibold">Cancel</button>
              <button
                onClick={runConsolidate}
                disabled={consBusy}
                data-testid="consolidate-confirm-button"
                className="lm-btn-primary px-4 py-2.5 rounded-xl text-sm font-bold inline-flex items-center gap-2 disabled:opacity-60"
              >
                {consBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Merge className="w-4 h-4" />}
                Run consolidation
              </button>
            </div>
          </div>
        </div>
      )}

      {dupOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setDupOpen(false)}>
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="lm-font font-extrabold text-[#11151C] text-lg flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-[#FF6B3D]" /> Duplicate hosting records
              </h3>
              <button onClick={() => setDupOpen(false)} data-testid="dup-modal-close"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-[#11151C]/65 mt-1">
              Hosting rows that share the same domain. Pick which row to <strong>keep</strong> — the others are dropped and any 20i link / stack user is promoted onto the keeper. Previous owners are added as shared users so they keep portal access.
            </p>

            {dupLoading && (
              <div className="py-12 text-center text-[#11151C]/60 text-sm">
                <Loader2 className="w-5 h-5 animate-spin mx-auto" /> Scanning…
              </div>
            )}

            {!dupLoading && dupGroups.length === 0 && (
              <div className="py-12 text-center text-[#11151C]/60 text-sm" data-testid="dup-empty">
                <CheckCircle2 className="w-6 h-6 mx-auto text-emerald-500" />
                <p className="mt-2 font-semibold text-[#11151C]">No duplicates found.</p>
                <p>Every domain has exactly one local hosting record.</p>
              </div>
            )}

            {!dupLoading && dupGroups.map((g) => (
              <div key={g.domain} className="mt-5 border border-[#FF6B3D]/25 rounded-xl p-4 bg-[#FFFBF6]" data-testid={`dup-group-${g.domain}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="font-bold text-[#11151C] inline-flex items-center gap-2">
                    <Globe className="w-4 h-4 text-[#FF6B3D]" /> {g.domain}
                    <span className="text-[11px] bg-[#FF6B3D]/15 text-[#B23B12] px-2 py-0.5 rounded-full font-semibold">{g.count} rows</span>
                  </div>
                  <button
                    onClick={() => mergeGroup(g)}
                    data-testid={`dup-merge-${g.domain}`}
                    className="lm-btn-primary inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-bold"
                  >
                    <Merge className="w-3.5 h-3.5" /> Merge into selected
                  </button>
                </div>
                <div className="mt-3 space-y-2">
                  {(g.items || []).map((it) => {
                    const isKeeper = dupKeeper[g.domain] === it.id;
                    return (
                      <label
                        key={it.id}
                        className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer border ${isKeeper ? "border-[#FF6B3D] bg-white" : "border-[#11151C]/10 bg-white/60 hover:border-[#FF6B3D]/40"}`}
                      >
                        <input
                          type="radio"
                          name={`keeper-${g.domain}`}
                          checked={isKeeper}
                          onChange={() => setDupKeeper({ ...dupKeeper, [g.domain]: it.id })}
                          data-testid={`dup-keep-${it.id}`}
                          className="mt-1 accent-[#FF6B3D]"
                        />
                        <div className="flex-1 text-sm">
                          <div className="font-semibold text-[#11151C]">{it.customer_name || "—"} <span className="text-[#11151C]/55 font-normal">· {it.customer_email}</span></div>
                          <div className="text-xs text-[#11151C]/60 mt-1 flex flex-wrap gap-x-3 gap-y-1">
                            <span>Plan: <strong className="text-[#11151C]">{it.plan_name || "—"}</strong></span>
                            <span>₹{it.amount_inr ?? 0}</span>
                            <span>Status: <strong className="text-[#11151C]">{it.status || "—"}</strong></span>
                            <span>Source: <strong className="text-[#11151C]">{it.imported_from || "—"}</strong></span>
                          </div>
                          <div className="text-xs mt-1.5 flex flex-wrap gap-x-3 gap-y-1">
                            <span className={it.twentyi_package_id ? "text-emerald-700" : "text-red-600 font-semibold"}>
                              {it.twentyi_package_id ? `20i pkg #${it.twentyi_package_id}` : "⚠ Not linked to 20i"}
                            </span>
                            <span className={it.stack_username ? "text-emerald-700" : "text-[#11151C]/40"}>
                              {it.stack_username ? `CP user: ${it.stack_username}` : "No CP user"}
                            </span>
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
                <p className="text-[11px] text-[#11151C]/55 mt-2">
                  Recommended: keep the row that already shows a 20i package id (pre-selected for you).
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
